/**
 * Validate an email address
 * @param email - Email to validate
 * @returns True if valid, false otherwise
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate a phone number (Danish format)
 * @param phone - Phone number to validate
 * @returns True if valid, false otherwise
 */
export const isValidPhone = (phone: string): boolean => {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Danish phone numbers are 8 digits
  return cleaned.length === 8;
};

/**
 * Validate a password (minimum requirements)
 * @param password - Password to validate
 * @param minLength - Minimum length (default: 8)
 * @returns True if valid, false otherwise
 */
export const isValidPassword = (password: string, minLength: number = 8): boolean => {
  return password.length >= minLength;
};

/**
 * Validate required field is not empty
 * @param value - Value to check
 * @returns True if not empty, false otherwise
 */
export const isNotEmpty = (value: string | null | undefined): boolean => {
  if (value === null || value === undefined) {
    return false;
  }
  return value.trim().length > 0;
};

/**
 * Validate a URL
 * @param url - URL to validate
 * @returns True if valid, false otherwise
 */
export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Validate a Danish postal code
 * @param postalCode - Postal code to validate
 * @returns True if valid, false otherwise
 */
export const isValidPostalCode = (postalCode: string): boolean => {
  // Danish postal codes are 4 digits
  const cleaned = postalCode.replace(/\D/g, '');
  return cleaned.length === 4;
};

/**
 * Create a validation function for a form
 * @param schema - Validation schema with field names and validation functions
 * @returns Validation function that returns errors for invalid fields
 */
export const createValidator = <T extends Record<string, any>>(
  schema: Record<keyof T, (value: any) => boolean>,
  errorMessages: Record<keyof T, string>
) => {
  return (values: T): Partial<Record<keyof T, string>> => {
    const errors: Partial<Record<keyof T, string>> = {};
    
    Object.keys(schema).forEach((key) => {
      const field = key as keyof T;
      const isValid = schema[field];
      
      if (!isValid(values[field])) {
        errors[field] = errorMessages[field];
      }
    });
    
    return errors;
  };
}; 